// User types
export interface User {
  id: number;
  email: string;
  full_name: string;
  phone_number?: string;
  government_id?: string;
  role: string;
  is_active: boolean;
  is_admin: boolean;
  is_buyer: boolean;
  is_seller: boolean;
  created_at: string;
  updated_at: string;
  // Email verification fields
  email_verified: boolean;
  verification_sent_at?: string;
}

export interface UserCreate {
  email: string;
  password: string;
  full_name: string;
  phone_number?: string;
  government_id?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_buyer?: boolean;
  is_seller?: boolean;
}

export interface UserUpdate {
  email?: string;
  password?: string;
  full_name?: string;
  phone_number?: string;
  government_id?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_buyer?: boolean;
  is_seller?: boolean;
}

// Company types
export interface Company {
  id: number;
  name: string;
  tax_id: string;
  industry?: string;
  address?: string;
  country?: string;
  phone?: string;
  email?: string;
  // New optional flags and scoring
  is_payer?: boolean;
  is_issuer?: boolean;
  score?: string;
  // Optional detailed contacts (backend naming)
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyCreate {
  name: string;
  tax_id: string;
  industry?: string;
  address?: string;
  country?: string;
  phone?: string;
  email?: string;
  // Optional backend-aligned fields
  is_payer?: boolean;
  is_issuer?: boolean;
  score?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface CompanyUpdate {
  name?: string;
  tax_id?: string;
  industry?: string;
  address?: string;
  country?: string;
  phone?: string;
  email?: string;
  // Optional backend-aligned fields
  is_payer?: boolean;
  is_issuer?: boolean;
  score?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

// Invoice types
export enum InvoiceStatus {
  PENDING = "pending",
  VERIFIED = "verified",
  SOLD = "sold",
  EXPIRED = "expired",
  REJECTED = "rejected",
  DELISTED = "delisted"
}

export interface Invoice {
  id: number;
  seller_id: number;
  company_id?: number;
  debtor_id?: number;  // Made optional - can be null until admin assigns company
  issuer_id?: number;
  invoice_number: string;
  timbrado?: string;
  issue_date: string;
  due_date: string;
  amount: number;
  tax_retention: number;
  tax_amount: number;
  net_amount: number;
  minimum_amount?: number;
  currency: string;
  description: string;
  status: InvoiceStatus | string; // Allow string for 'unknown' status
  front_image_path: string;
  has_permission_issue?: boolean; // Added for UI handling of permission errors
  back_image_path?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  highest_bid?: number;
  debtor?: Company;
  issuer?: Company;

  // Admin score override fields
  score_override_score?: number | null;
  score_override_reason?: string | null;
  score_override_by_id?: number | null;
  score_override_at?: string | null;

  // Effective (server-computed) score
  effective_score?: number | null;
  effective_grade?: ScoreGrade | null;
  score_source?: 'auto' | 'override' | null;

  // Admin-controlled visibility
  score_visible?: boolean;
}

export interface InvoiceWithRelations extends Invoice {
  seller: User;
  company?: Company;
  issuer?: Company;
  bids?: Bid[];
  highest_bid?: number;
}

export interface InvoiceCreate {
  debtor_id?: number;  // Optional - admin assigns during verification
  issuer_id?: number;
  invoice_number: string;
  timbrado?: string;
  issue_date: string;
  due_date: string;
  amount: string | number;
  tax_retention: number;
  tax_amount: string | number;
  minimum_amount?: string | number;
  currency?: string;
  description: string;
}

export interface InvoiceAdminCreate extends InvoiceCreate {
  seller_id: number;
  status?: InvoiceStatus;
}

export interface InvoiceUpdate {
  company_id?: number;
  debtor_id?: number;  // Allow admin to assign/change debtor company
  issuer_id?: number;
  invoice_number?: string;
  timbrado?: string;
  issue_date?: string;
  due_date?: string;
  amount?: string | number;
  minimum_amount?: string | number;
  tax_retention?: number;
  tax_amount?: string | number;
  currency?: string;
  description?: string;
  status?: InvoiceStatus;
  score_visible?: boolean;
}

// Invoice Score types
export type ScoreGrade = 'AA' | 'A' | 'B' | 'C' | 'D';
export type ScoreConfidence = 'high' | 'medium' | 'low';

export interface InvoiceScore {
  id: number;
  invoice_id: number;
  score: number;
  grade: ScoreGrade;
  debtor_score: number;
  invoice_quality_score: number;
  seller_score: number;
  market_score: number;
  confidence: ScoreConfidence;
  risk_flags: string[] | null;
  computed_at: string;
  version: string;
}

export interface InvoiceScoreOverrideInfo {
  score: number;
  grade: ScoreGrade;
  reason: string;
  by_id?: number | null;
  by_email?: string | null;
  by_name?: string | null;
  at?: string | null;
}

export interface InvoiceScoreBundle {
  auto: InvoiceScore | null;
  override: InvoiceScoreOverrideInfo | null;
  effective_score: number | null;
  effective_grade: ScoreGrade | null;
  source: 'auto' | 'override' | null;
}

// Bid types
export enum BidStatus {
  ACTIVE = "active",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  EXPIRED = "expired"
}

export interface Bid {
  id: number;
  buyer_id: number;
  invoice_id: number;
  amount: number;
  status: BidStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface BidWithRelations extends Bid {
  buyer: User;
  invoice: Invoice;
}

export interface BidCreate {
  invoice_id: number;
  amount: number;
}

export interface BidUpdate {
  amount?: number;
  status?: BidStatus;
}

// Transaction types
export enum TransactionStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled"
}

export interface Transaction {
  id: number;
  amount: number;
  status: string;
  seller_id: number;
  buyer_id: number;
  invoice_id: number;
  bid_id: number;
  notes?: string;
  completed_at: string;
  invoice?: Invoice;
}

export interface TransactionWithRelations extends Transaction {
  seller: User;
  buyer: User;
  invoice: Invoice;
  bid: Bid;
}

export interface TransactionCreate {
  bid_id?: number;
  invoice_id?: number;
  amount: number;
  currency: string;
  transaction_type: string;
  description?: string;
  payment_details?: any;
}

// Analytics dashboard types
export interface DailyMetric {
  date: string;
  new_users: number;
  invoices_uploaded: number;
  invoices_verified: number;
  bids_placed: number;
  bids_accepted: number;
  total_bid_value: number;
}

export interface DashboardPeriod {
  start_date: string;
  end_date: string;
  days: number;
}

export interface DashboardTotals {
  users: number;
  sellers: number;
  buyers: number;
  invoices: number;
  verified_invoices: number;
  sold_invoices: number;
  bids: number;
  accepted_bids: number;
}

export interface DashboardStats {
  period: DashboardPeriod;
  totals: DashboardTotals;
  recent_activity: {
    events_last_7_days: number;
  };
  daily_metrics: DailyMetric[];
}

// Authentication types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface Token {
  access_token: string;
  token_type: string;
}

export interface TokenPayload {
  sub: string;
  exp: number;
}
